import React, { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import { Link } from 'react-router-dom';

function ApredizTicketsTable() {
    const [loading, setLoading] = useState(false);
    const [ticketsData, setTicketsData] = useState(null);
    const [page, setPage] = useState(1);
    const [itemsPerPage] = useState(8);
    const [totalItems, setTotalItems] = useState(0);
    const [ticketsError, setTicketsError] = useState(null);
    const doc = localStorage.getItem('userDocument');

    const handleLoad = useCallback(async () => {
        try {
            setLoading(true);
            const response = await authenticatedFetch(`${API_URL}/v1/Tickets-by-user/${doc}`, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Error en la solicitud');
            }
            const data = await response.json();
            setTicketsData(data);
            setTotalItems(data.length);
            setLoading(false);

        } catch (error) {
            console.error('Error:', error);
            setTicketsError(error.message);
        }
    }, [doc]);

    useEffect(() => {
        if (!ticketsData) {
            handleLoad();
        }
    }, [ticketsData, handleLoad]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const tickets = ticketsData ? ticketsData.slice((page - 1) * itemsPerPage, page * itemsPerPage) : [];

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="aprendiz-tickets-container">
            {loading && (
                <div className="flex items-center justify-center p-4" data-testid="aprendiz-tickets-loading">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-700"></div>
                    <span className="ml-2">Cargando tickets...</span>
                </div>
            )}

            {!loading && ticketsData && (
                <>
                    <div className="mt-6 mb-6 flex justify-center" data-testid="aprendiz-tickets-create-button-container">
                        <Link 
                            to="/CreateTicket" 
                            className="px-6 py-3 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                            data-testid="aprendiz-tickets-create-button"
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                            <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                            <span className="z-10 text-gray-900">Crear Nuevo Ticket</span>
                        </Link>
                    </div>

                    <div className="overflow-x-auto shadow-md rounded-lg" data-testid="aprendiz-tickets-table-container">
                        <table className="min-w-full divide-y divide-gray-300 rounded-lg overflow-hidden" data-testid="aprendiz-tickets-table">
                            <thead className="bg-gray-50" data-testid="aprendiz-tickets-table-head">
                                <tr>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-tickets-table-header-user">Usuario</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell" data-testid="aprendiz-tickets-table-header-description">Descripción</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-tickets-table-header-status">Estado</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell" data-testid="aprendiz-tickets-table-header-date">Fecha</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-tickets-table-header-options">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-300" data-testid="aprendiz-tickets-table-body">
                                {tickets.map((ticket) => (
                                    <tr key={ticket.ticket_id} className="hover:bg-gray-50" data-testid={`aprendiz-tickets-row-${ticket.ticket_id}`}>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`aprendiz-tickets-cell-user-${ticket.ticket_id}`}>
                                            {ticket.aprendiz_name}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell" data-testid={`aprendiz-tickets-cell-description-${ticket.ticket_id}`}>
                                            {ticket.ticket_description}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap" data-testid={`aprendiz-tickets-cell-status-${ticket.ticket_id}`}>
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${ticket.status === 1 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
                                                data-testid={`aprendiz-tickets-status-badge-${ticket.ticket_id}`}
                                            >
                                                {ticket.status === 1 ? 'Nuevo' : 'Atendido'}
                                            </span>
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell" data-testid={`aprendiz-tickets-cell-date-${ticket.ticket_id}`}>
                                            {formatDate(ticket.create_date)}
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`aprendiz-tickets-cell-options-${ticket.ticket_id}`}>
                                            <Link to={`/Ticket/id/${ticket.ticket_id}`} className="text-amber-700 hover:text-amber-900 font-medium" data-testid={`aprendiz-tickets-view-link-${ticket.ticket_id}`}>
                                                Ver
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {tickets.length > 0 && (
                        <div className="mt-6 mb-4 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="aprendiz-tickets-pagination">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="w-full sm:w-auto px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="aprendiz-tickets-prev-button"
                            >
                                <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                                <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                                <span className="z-10 text-gray-900">Anterior</span>
                            </button>
                            
                            <span className="mx-2 py-1 text-sm text-center" data-testid="aprendiz-tickets-page-indicator">
                                Página {page} de {Math.ceil(totalItems / itemsPerPage)}
                            </span>
                            
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= Math.ceil(totalItems / itemsPerPage)}
                                className="w-full sm:w-auto px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="aprendiz-tickets-next-button"
                            >
                                <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / itemsPerPage) ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                                <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / itemsPerPage) ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                                <span className="z-10 text-gray-900">Siguiente</span>
                            </button>
                        </div>
                    )}
                </>
            )}

            {ticketsError && (
                <div className="text-center text-red-600 p-4" data-testid="aprendiz-tickets-error">
                    Error al cargar los tickets: {ticketsError}
                </div>
            )}

            {!loading && ticketsData && tickets.length === 0 && (
                <div className="text-center text-gray-600 p-4" data-testid="aprendiz-tickets-empty">
                    No tienes tickets registrados.
                </div>
            )}
        </div>
    );
}

export { ApredizTicketsTable };
